import {useContext, useRef, useCallback} from 'react';
import type L from 'leaflet';
import {MapWeatherPoint} from '@/app/interfaces/map';
import {RouteName} from '@/app/constants/routing';
import {TemperatureUnit} from '@/common/constants/units';
import {useRouter} from '@/common/services/router';
import {useUrlBuilder} from '@/app/routing/useUrlBuilder';
import {useLocalizer} from '@/common/services/localizer';
import {temperatureDisplayReduce} from '@/common/helpers/unitReducers/temperatureDisplayReduce';
import {ServicesContext} from '@/app/contexts/ServicesContext';
import styles from './WeatherMap.css';

const pixelRatio = typeof devicePixelRatio !== 'undefined' && devicePixelRatio > 1 ? '2x' : '1x';
const getIconUrl = (condition: number, isNight: boolean) => {
	// eslint-disable-next-line @typescript-eslint/no-require-imports,@typescript-eslint/no-var-requires
	return require(`./icons/conditions/${pixelRatio}/${isNight ? 'n' : 'd'}${condition}.png`).default;
};

const getMarkerElementPrototype = () => {
	const rootElement = document.createElement('div');
	rootElement.className = styles.pointRoot;

	rootElement.innerHTML = `
		<div class="${styles.pointEntry}">
			<div class="${styles.pointIcon}"></div>
			<div class="${styles.pointTemp}"></div>
			<div class="${styles.pointTitle}"></div>
			<div class="${styles.pointTempWater}">
				<span></span>
				<strong></strong>
			</div>
		</div>
		<div class="${styles.pointHint}">
			<strong></strong>
			<div></div>
		</div>
	`;

	return rootElement;
};

const fillMarker = ({
	markerElement,
	point: {location, country, weather, isNight},
	temperatureUnit,
	inverseHorizontalExpand,
	inverseVerticalExpand,
	t,
}: {
	markerElement: HTMLElement;
	point: MapWeatherPoint;
	temperatureUnit: TemperatureUnit;
	inverseHorizontalExpand: boolean;
	inverseVerticalExpand: boolean;
	t(languageCode: string): string;
}) => {
	const select = <T extends HTMLElement>(selector: string) => markerElement.querySelector(selector) as T;

	// Fill main data
	select(`.${styles.pointIcon}`).style.backgroundImage = `url(${getIconUrl(weather.condition, isNight)})`;
	select(`.${styles.pointTemp}`).textContent = temperatureDisplayReduce(weather.temp, temperatureUnit);
	select(`.${styles.pointTitle}`).textContent = location.title;
	select(`.${styles.pointHint} > strong`).textContent = `${location.title}, ${country}`;
	select(`.${styles.pointHint} > div`).textContent = t(`weatherConditions.${weather.condition}`);

	// FIll water temperature or remove the element whe there is no value
	if (weather.tempWater) {
		const tempValue = temperatureDisplayReduce(weather.tempWater, temperatureUnit, true);
		select(`.${styles.pointTempWater} > span`).textContent = t('waterTemp');
		select(`.${styles.pointTempWater} > strong`).textContent = tempValue;
	} else {
		const element = select(`.${styles.pointTempWater}`);
		element.parentElement!.removeChild(element);
	}

	// Mark admin centers location
	if (location.flags.countryAdminCenter || location.flags.regionAdminCenter || location.flags.districtAdminCenter) {
		select(`.${styles.pointTitle}`).classList.add(styles.isGreat);
	}

	// Set style to control hint expand position
	if (inverseHorizontalExpand) {
		select(`.${styles.pointHint}`).classList.add(styles.expandToLeft);
	}
	if (inverseVerticalExpand) {
		select(`.${styles.pointHint}`).classList.add(styles.expandToTop);
	}
};

export function useMapPoints({temperatureUnit}: {temperatureUnit: TemperatureUnit}) {
	const {backendAPI} = useContext(ServicesContext)!;

	const {history} = useRouter();
	const urlBuilder = useUrlBuilder();

	const t = useLocalizer();
	const {language} = t;

	const environmentRef = useRef<{leaflet: typeof L; map: L.Map} | null>();
	const initialize = useCallback((leaflet: typeof L, map: L.Map) => {
		environmentRef.current = {leaflet, map};
	}, []);

	const currentMarkersRef = useRef<L.Marker[]>([]);
	const markersUpdatedAtRef = useRef<number | null>(null);

	const resetMarkers = useCallback(() => {
		for (const marker of currentMarkersRef.current) {
			marker.off().remove();
		}
	}, []);

	const setMarkers = useCallback(async () => {
		// TODO cancel previous request;

		markersUpdatedAtRef.current = Date.now();

		const {leaflet, map} = environmentRef.current!;

		// Prepare parameters to fetch points
		const zoom = map.getZoom();
		const boundsSource = map.getBounds();
		const bounds = {
			northeast: boundsSource.getNorthEast().wrap(),
			southwest: boundsSource.getSouthWest().wrap(),
		};

		// Fetch points
		const pointsData = await backendAPI.map.getPoints(bounds, zoom, language);

		// Transform map point coordinates to for map compatible format (can overflow -180/180)
		const mapWest = boundsSource.getWest();
		const mapEast = boundsSource.getEast();
		for (const point of pointsData) {
			if (point.coordinates.lng < mapWest) {
				point.coordinates.lng += (Math.trunc((mapWest - point.coordinates.lng) / 360) + 1) * 360;
			} else if (point.coordinates.lng > mapEast) {
				point.coordinates.lng -= (Math.trunc((point.coordinates.lng - mapEast) / 360) + 1) * 360;
			}
		}

		// Avoid requests race condition bugs
		if (map.getZoom() !== zoom) {
			return;
		}

		// Create map markers
		const markerElementPrototype = getMarkerElementPrototype();
		const markers = pointsData.map(point => {
			// Create a map marker
			const markerElement = markerElementPrototype.cloneNode(true) as HTMLDivElement;
			const marker = leaflet.marker(point.coordinates, {
				icon: leaflet.divIcon({html: markerElement, className: styles.pointWrapper}),
			});

			// Determinate the marker expansion direction
			const mapContainer = map.getContainer();
			const mapPixelWidth = mapContainer.clientWidth;
			const mapPixelHeight = mapContainer.clientHeight;
			const markerPixelPosition = map.latLngToContainerPoint(point.coordinates);
			const inverseHorizontalExpand = markerPixelPosition.x + 200 > mapPixelWidth;
			const inverseVerticalExpand = markerPixelPosition.y + 100 > mapPixelHeight;

			// Fill the marker data
			fillMarker({
				markerElement,
				point,
				temperatureUnit,
				inverseHorizontalExpand,
				inverseVerticalExpand,
				t,
			});

			// Handle navigation to a location on click
			marker.on('click', () => {
				const url = urlBuilder(RouteName.weather, {location: point.location});
				history!.push(url);
			});

			marker.addTo(map);

			return marker;
		});

		// Remove the previous markers
		resetMarkers();

		currentMarkersRef.current = markers;
	}, [language, temperatureUnit]);

	const getMarkersUpdateTime = () => {
		return markersUpdatedAtRef.current;
	};

	return {initialize, setMarkers, resetMarkers, getMarkersUpdateTime};
}

useMapPoints.displayName = 'useMapPoints';
